import { Avatar } from "../Avatar/index.js";
import { Button } from "../Button/index.js";
import { Tooltip } from "../Tooltip/index.js";
import { setSnack } from "../SnackbarCenter/index.js";
import { Popover, usePopover } from "../Popover/index.js";
import { memo } from "react";
import { IconCheckmark, IconClose, IconFileCopy } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/UserInformation/index.tsx
const containerClassName = "relative flex max-w-[320px] py-3 pl-0 pr-2 outline-none";
function formatUserFullName(user) {
	const middle = user.middleName ?? user.givenName ?? "";
	return [
		user.lastName,
		user.firstName,
		middle
	].filter(Boolean).join(" ").trim();
}
function getUserInitials(user) {
	return `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`;
}
const copyRowTextClassName = {
	userName: "subtitle-base pr-1 not-italic",
	group: "paragraph-base text-brand-40 not-italic",
	position: "subtitle-base pr-1 not-italic"
};
function showCopiedSnack() {
	setSnack({
		title: "Скопировано в буфер обмена",
		icon: /* @__PURE__ */ jsx(IconCheckmark, { className: "size-4" }),
		messageType: "info"
	});
}
const CopyableRow = ({ text, variant, onCopy }) => /* @__PURE__ */ jsxs("div", {
	onClick: () => {
		navigator.clipboard.writeText(text);
		onCopy(text);
		showCopiedSnack();
	},
	className: "hover:bg-neutrals-99 group flex w-full cursor-pointer items-center justify-between transition-opacity duration-75",
	children: [/* @__PURE__ */ jsx("span", {
		"data-cy": variant === "userName" ? "userInformation-span-tasktracker-userName" : variant === "group" ? "userInformation-span-tasktracker-group" : "userInformation-span-tasktracker-position",
		className: copyRowTextClassName[variant],
		children: text
	}), /* @__PURE__ */ jsx(IconFileCopy, { className: "text-neutrals-60 size-4 opacity-0 transition-opacity duration-75 group-hover:opacity-100" })]
});
const UserInformationContent = memo(({ user, enabledUser, onCopy }) => {
	const { closePopover } = usePopover();
	if (!user) return /* @__PURE__ */ jsxs("div", {
		className: containerClassName,
		children: [
			/* @__PURE__ */ jsx("div", {
				className: "-ml-5",
				children: /* @__PURE__ */ jsx(Avatar, {
					alt: "Загрузка пользователя",
					className: "border-2 border-white"
				})
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "ml-2.5 mr-1 flex flex-1 flex-col gap-1.5",
				children: [
					/* @__PURE__ */ jsx("div", { className: "bg-neutrals-95 h-4 w-48 animate-pulse rounded" }),
					/* @__PURE__ */ jsx("div", { className: "bg-neutrals-95 h-4 w-48 animate-pulse rounded" }),
					/* @__PURE__ */ jsx("div", { className: "bg-neutrals-95 h-4 w-48 animate-pulse rounded" })
				]
			}),
			/* @__PURE__ */ jsx(Button, {
				theme: "text",
				className: "h-[25px] p-0 focus:outline-none",
				onClick: closePopover,
				children: /* @__PURE__ */ jsx(IconClose, {
					"data-cy": "userInformation-iconClose-tasktracker",
					className: "text-neutrals-60 size-5"
				})
			})
		]
	});
	const fullName = formatUserFullName(user);
	return /* @__PURE__ */ jsxs("div", {
		className: containerClassName,
		children: [
			/* @__PURE__ */ jsx("div", {
				className: "-ml-5",
				children: /* @__PURE__ */ jsx(Avatar, {
					alt: fullName || "Пользователь",
					initials: getUserInitials(user),
					className: "border-2 border-white"
				})
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "ml-2.5 mr-1 flex flex-1 flex-col gap-1.5",
				children: [
					/* @__PURE__ */ jsx(CopyableRow, {
						text: fullName,
						variant: "userName",
						onCopy
					}),
					enabledUser === false ? /* @__PURE__ */ jsx("p", {
						className: "paragraph-base text-system-error-70 italic",
						children: "Пользователь отключён"
					}) : null,
					user.email ? /* @__PURE__ */ jsx(CopyableRow, {
						text: user.email,
						variant: "position",
						onCopy
					}) : null,
					user.groups?.[0]?.name ? /* @__PURE__ */ jsx(CopyableRow, {
						text: user.groups[0].name,
						variant: "group",
						onCopy
					}) : null,
					user.position && /* @__PURE__ */ jsx(CopyableRow, {
						text: user.position,
						variant: "position",
						onCopy
					})
				]
			}),
			/* @__PURE__ */ jsx(Button, {
				theme: "text",
				className: "h-[25px] p-0 focus:outline-none",
				onClick: closePopover,
				children: /* @__PURE__ */ jsx(IconClose, {
					"data-cy": "userInformation-iconClose-tasktracker",
					className: "text-neutrals-60 size-5"
				})
			})
		]
	});
});
UserInformationContent.displayName = "UserInformationContent";
/**
* Карточка пользователя в Popover.
*/
const UserInformation = memo(({ trigger, user, start, inlineBlock, disablePortal = false, disableTopMargin = false, className, deletedUser, enabledUser = true, onCopy }) => {
	const handleCopy = (text) => {
		onCopy?.(text);
	};
	if (deletedUser) return /* @__PURE__ */ jsx(Tooltip, {
		description: "Пользователь удалён из системы",
		children: /* @__PURE__ */ jsx("span", {
			className: "text-system-error-70 italic line-through",
			children: "Удалённый пользователь"
		})
	});
	const triggerWrapper = /* @__PURE__ */ jsx("div", {
		"data-cy": "userInformation-div-tasktracker",
		tabIndex: 0,
		className: cn("relative inline-block cursor-pointer outline-none", !disableTopMargin && "mt-1.5", "hover:outline-neutrals-90 hover:bg-neutrals-95 hover:w-fit hover:rounded hover:outline hover:outline-2 hover:outline-offset-0", user?.enabled === false && "text-system-error-70", className),
		children: trigger
	});
	return /* @__PURE__ */ jsx(Popover, {
		inlineBlock,
		trigger: triggerWrapper,
		disablePortal,
		align: start ? "start" : "center",
		isArrow: true,
		children: /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(UserInformationContent, {
			user,
			enabledUser,
			onCopy: handleCopy
		}) })
	});
});
UserInformation.displayName = "UserInformation";
//#endregion
export { UserInformation };

//# sourceMappingURL=index.js.map